
(() => {
  'use strict';

  /* =========================================
     PICKLEPROSHOP — AUTOMATIC FREE GIFTS

     1. Buy 2 Christmas ornament sets, get 1 free.
     2. Buy 2 V40 Pro 4-packs, get 1 free.
     3. Buy 2 V40 Pro 12-packs, get 1 V40 Pro 4-pack free.
     4. Buy 2 VX40 Junior 12-packs, get 1 Junior 4-pack free.
     5. Buy 2 VX40 Junior 4-packs, get 1 free.
     6. Buy 1 Neoprene Paddle Cover, get 1 free.
  ========================================= */

  const CONFIG = {
    ornaments: {
      variantId: 49906415337714,
      requiredPaidQuantity: 2,
      giftProperty: '_auto_christmas_gift',
      giftPropertyValue: 'true'
    },

    offers: [
      {
        name: 'V40 Pro 4-pack',
        buyHandle:
          'picklepro-max-40-hole-4-piece-usap-approved-pickleball-balls',
        giftHandle:
          'picklepro-max-40-hole-4-piece-usap-approved-pickleball-balls',
        requiredPaidQuantity: 2,
        giftProperty: '_auto_v40_4pack_gift',
        giftPropertyValue: 'true'
      },
      {
        name: 'V40 Pro 12-pack',
        buyHandle: 'max-pickleballs-12-pack',
        giftHandle:
          'picklepro-max-40-hole-4-piece-usap-approved-pickleball-balls',
        requiredPaidQuantity: 2,
        giftProperty: '_auto_v40_12pack_gift',
        giftPropertyValue: 'true'
      },
      {
        name: 'VX40 Junior 12-pack',
        buyHandle:
          'picklepro-junior-vx40-pickleballs-indoor-outdoor',
        giftHandle:
          'picklepro-junior-40-hole-4-pack-pickleballs-usap-approved',
        requiredPaidQuantity: 2,
        giftProperty: '_auto_junior_12pack_gift',
        giftPropertyValue: 'true'
      },
      {
        name: 'VX40 Junior 4-pack',
        buyHandle:
          'picklepro-junior-40-hole-4-pack-pickleballs-usap-approved',
        giftHandle:
          'picklepro-junior-40-hole-4-pack-pickleballs-usap-approved',
        requiredPaidQuantity: 2,
        giftProperty: '_auto_junior_4pack_gift',
        giftPropertyValue: 'true'
      },
      {
        name: 'Neoprene Paddle Cover',
        buyHandle: 'picklepro-universal-neoprene-paddle-cover',
        giftHandle: 'picklepro-universal-neoprene-paddle-cover',
        requiredPaidQuantity: 1,
        giftProperty: '_auto_neoprene_cover_gift',
        giftPropertyValue: 'true'
      }
    ],

    loaderId: 'picklepro-free-gift-loading'
  };

  const ROOT = window.Shopify?.routes?.root || '/';

  let syncing = false;
  let syncRequested = false;
  let syncTimer = null;

  const productCache = new Map();

  /* =========================================
     CART API
  ========================================= */

  async function cartRequest(endpoint, options = {}) {
    const response = await fetch(ROOT + endpoint, {
      credentials: 'same-origin',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${endpoint} (${response.status})`
      );
    }

    return response.json();
  }

  const getCart = () => cartRequest('cart.js');

  function changeLine(key, quantity) {
    return cartRequest('cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity })
    });
  }

  function addGift(variantId, quantity, property, value) {
    if (quantity <= 0) return Promise.resolve(null);

    return cartRequest('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity,
          properties: { [property]: value }
        }]
      })
    });
  }

  function isGift(item, property, value) {
    return item.properties?.[property] === value;
  }

  function isAutomaticGift(item) {
    if (
      isGift(
        item,
        CONFIG.ornaments.giftProperty,
        CONFIG.ornaments.giftPropertyValue
      )
    ) {
      return true;
    }

    return CONFIG.offers.some(offer =>
      isGift(item, offer.giftProperty, offer.giftPropertyValue)
    );
  }

  /* =========================================
     PRODUCT DATA
  ========================================= */

  async function getProduct(handle) {
    if (!productCache.has(handle)) {
      const promise = cartRequest(`products/${handle}.js`)
        .catch(error => {
          productCache.delete(handle);
          throw error;
        });

      productCache.set(handle, promise);
    }

    return productCache.get(handle);
  }

  async function getAvailableGiftVariant(handle) {
    const product = await getProduct(handle);
    const variant = (product.variants || []).find(v => v.available);

    if (!variant) {
      throw new Error(`No available gift variant: ${handle}`);
    }

    return Number(variant.id);
  }

  /* =========================================
     LOADER
  ========================================= */

  function getCartDrawer() {
    return document.querySelector(
      'cart-drawer, #CartDrawer, .cart-drawer, [data-cart-drawer]'
    );
  }

  function showGiftLoader() {
    if (document.getElementById(CONFIG.loaderId)) return;

    const drawer = getCartDrawer();
    if (!drawer) return;

    const loader = document.createElement('div');
    loader.id = CONFIG.loaderId;
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');

    loader.style.cssText = [
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'gap:10px',
      'padding:12px 14px',
      'margin:10px 0',
      'border:1px solid #e8e8e8',
      'border-radius:8px',
      'background:#fffaf0',
      'color:#222',
      'font-size:14px',
      'font-weight:600',
      'line-height:1.4'
    ].join(';');

    const spinner = document.createElement('span');
    spinner.style.cssText = [
      'width:16px',
      'height:16px',
      'border:2px solid #dedede',
      'border-top-color:#111',
      'border-radius:50%',
      'animation:pickleProGiftSpin .7s linear infinite',
      'flex-shrink:0'
    ].join(';');

    spinner.setAttribute('aria-hidden', 'true');

    const message = document.createElement('span');
    message.textContent = 'Adding your free gift...';

    loader.append(spinner, message);

    const target =
      drawer.querySelector('.drawer__inner') ||
      drawer.querySelector('.cart-drawer__content') ||
      drawer;

    target.prepend(loader);
  }

  function hideGiftLoader() {
    document.getElementById(CONFIG.loaderId)?.remove();
  }

  if (!document.getElementById('picklepro-gift-loader-styles')) {
    const style = document.createElement('style');
    style.id = 'picklepro-gift-loader-styles';
    style.textContent = `
      @keyframes pickleProGiftSpin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  /* =========================================
     CHRISTMAS ORNAMENT OFFER
     Existing settings unchanged.
  ========================================= */

  async function syncOrnaments() {
    const cfg = CONFIG.ornaments;
    const cart = await getCart();

    const lines = cart.items.filter(
      item => Number(item.variant_id) === Number(cfg.variantId)
    );

    const paidQuantity = lines
      .filter(item => !isGift(
        item, cfg.giftProperty, cfg.giftPropertyValue
      ))
      .reduce((total, item) => total + item.quantity, 0);

    const requiredGiftQuantity = Math.floor(
      paidQuantity / cfg.requiredPaidQuantity
    );

    const giftLines = lines.filter(item =>
      isGift(item, cfg.giftProperty, cfg.giftPropertyValue)
    );

    const currentGiftQuantity = giftLines.reduce(
      (total, item) => total + item.quantity, 0
    );

    if (currentGiftQuantity === requiredGiftQuantity) return false;

    showGiftLoader();

    for (const line of giftLines) {
      await changeLine(line.key, 0);
    }

    if (requiredGiftQuantity > 0) {
      await addGift(
        cfg.variantId,
        requiredGiftQuantity,
        cfg.giftProperty,
        cfg.giftPropertyValue
      );
    }

    return true;
  }

  /* =========================================
     OTHER PRODUCT OFFERS
  ========================================= */

  async function syncOffer(offer, cart) {
    const paidLines = cart.items.filter(item =>
      item.handle === offer.buyHandle && !isAutomaticGift(item)
    );

    const paidQuantity = paidLines.reduce(
      (total, item) => total + item.quantity, 0
    );

    const requiredGiftQuantity = Math.floor(
      paidQuantity / offer.requiredPaidQuantity
    );

    const giftLines = cart.items.filter(item =>
      isGift(item, offer.giftProperty, offer.giftPropertyValue)
    );

    const currentGiftQuantity = giftLines.reduce(
      (total, item) => total + item.quantity, 0
    );

    if (currentGiftQuantity === requiredGiftQuantity) return false;

    showGiftLoader();

    for (const line of giftLines) {
      await changeLine(line.key, 0);
    }

    if (requiredGiftQuantity <= 0) return true;

    // Same product as the purchased item: preserve its variant.
    if (offer.buyHandle === offer.giftHandle) {
      const variantQuantities = new Map();

      for (const item of paidLines) {
        const id = Number(item.variant_id);
        variantQuantities.set(
          id,
          (variantQuantities.get(id) || 0) + item.quantity
        );
      }

      let remaining = requiredGiftQuantity;

      for (const [variantId, quantity] of variantQuantities) {
        const giftQuantity = Math.min(
          Math.floor(quantity / offer.requiredPaidQuantity),
          remaining
        );

        if (giftQuantity > 0) {
          await addGift(
            variantId,
            giftQuantity,
            offer.giftProperty,
            offer.giftPropertyValue
          );

          remaining -= giftQuantity;
        }
      }

      // If quantities were split across variants, assign any remainder
      // to a purchased variant so the required gift quantity is fulfilled.
      if (remaining > 0 && paidLines.length) {
        await addGift(
          Number(paidLines[0].variant_id),
          remaining,
          offer.giftProperty,
          offer.giftPropertyValue
        );
      }
    } else {
      // Different product: select an available gift variant.
      const giftVariantId = await getAvailableGiftVariant(offer.giftHandle);

      await addGift(
        giftVariantId,
        requiredGiftQuantity,
        offer.giftProperty,
        offer.giftPropertyValue
      );
    }

    return true;
  }

  /* =========================================
     MAIN SYNCHRONIZATION
  ========================================= */

  async function syncChristmasGift() {
    if (syncing) {
      syncRequested = true;
      return;
    }

    syncing = true;

    try {
      do {
        syncRequested = false;

        await syncOrnaments();

        for (const offer of CONFIG.offers) {
          const cart = await getCart();
          await syncOffer(offer, cart);
        }

        const updatedCart = await getCart();

        document.dispatchEvent(new CustomEvent('christmas-gift:updated', {
          detail: { cart: updatedCart }
        }));

        document.dispatchEvent(new CustomEvent('picklepro-free-gifts:updated', {
          detail: { cart: updatedCart }
        }));

      } while (syncRequested);

    } catch (error) {
      console.error('[PickleProShop Free Gifts]', error);

      document.dispatchEvent(new CustomEvent('christmas-gift:error', {
        detail: { error }
      }));

    } finally {
      syncing = false;
      hideGiftLoader();

      if (syncRequested) {
        syncRequested = false;
        syncChristmasGift();
      }
    }
  }

  /* =========================================
     FASTER ADD-TO-CART DETECTION

     Observe successful theme/app cart additions instead of
     relying only on fixed click delays.
  ========================================= */

  function isCartAddRequest(input, init) {
    let url = '';

    try {
      url = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input?.url || '';
    } catch (_) {}

    const fullUrl = new URL(url, window.location.origin);
    const path = fullUrl.pathname;

    if (!/\/cart\/add(?:\.js)?$/.test(path)) return false;

    // Do not react to this script's own automatic gift requests.
    const body = init?.body;

    if (typeof body === 'string' && body.includes('_auto_')) {
      return false;
    }

    return true;
  }

  // Preserve the original fetch and react when an external add succeeds.
  if (!window.__pickleProGiftFetchHooked) {
    window.__pickleProGiftFetchHooked = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async function(input, init) {
      const shouldSync = isCartAddRequest(input, init);
      const response = await originalFetch(input, init);

      if (shouldSync && response.ok) {
        // Schedule immediately after the add-to-cart request succeeds.
        setTimeout(() => syncChristmasGift(), 0);
      }

      return response;
    };
  }

  function scheduleSync(delay = 50) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncChristmasGift, delay);
  }

  window.syncChristmasGift = syncChristmasGift;
  window.showChristmasGiftLoader = showGiftLoader;
  window.hideChristmasGiftLoader = hideGiftLoader;

  // Product form fallback for themes/apps that don't use fetch.
  document.addEventListener('submit', event => {
    const form = event.target;

    if (
      form instanceof HTMLFormElement &&
      /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.action)
    ) {
      scheduleSync(100);
    }
  }, true);

  // Button fallback. The successful request hook above is the primary trigger.
  document.addEventListener('click', event => {
    const button = event.target.closest(
      'button[name="add"], .product-form__submit, [data-add-to-cart]'
    );

    if (button) scheduleSync(150);
  }, true);

  document.addEventListener('change', event => {
    if (event.target.matches(
      'input[name="updates[]"], input[name^="updates"], ' +
      'cart-drawer input[type="number"]'
    )) {
      scheduleSync(50);
    }
  }, true);

  [
    'cart:updated',
    'cart:refresh',
    'cart-drawer:updated',
    'cart:change'
  ].forEach(eventName => {
    document.addEventListener(eventName, () => scheduleSync(50));
  });

  // Initial cart reconciliation.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => scheduleSync(100),
      { once: true }
    );
  } else {
    scheduleSync(100);
  }
})();
